import { useQuery } from 'react-query';
import { useDebounce } from 'hooks/useDebounced';
import { fetchPolygonsByZone } from 'services/API/BackendAPI';
import PolygonsAPI from 'services/API/PolygonsAPI';
import { useMapBoundsAsCardinalDirections } from 'state/mapSettings';
import { useZoomQuery } from 'state/queryParams';
import { ZOOM_THRESHOLD } from 'utils/constants';
import { formatBounds } from 'utils/format';

export const useParcelPolygon = () => {
  const [zoomQuery] = useZoomQuery();
  const [mapBoundsAsCardinalDirections] = useMapBoundsAsCardinalDirections();

  const debouncedValues = useDebounce(
    {
      mapBoundsAsCardinalDirections,
      zoomQuery,
    },
    400, // 400 is minimum to prevent zoom and bound conflict
  );

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: [
      'fetchPolygonsForMap',
      debouncedValues.mapBoundsAsCardinalDirections,
      debouncedValues.zoomQuery,
    ],
    queryFn: async () => {
      const bbox = formatBounds(mapBoundsAsCardinalDirections) || undefined;
      if (!bbox) {
        return [];
      }
      // Split the bbox string into an array of coordinate pairs (as strings)
      const coords = bbox.split(',').map((coord) => coord.split(' ').map(Number));

      // Calculate min and max for lat and long
      const longitudes = coords.map((coord) => coord[0]);
      const latitudes = coords.map((coord) => coord[1]);
      const min_long = Math.min(...longitudes);
      const max_long = Math.max(...longitudes);
      const min_lat = Math.min(...latitudes);
      const max_lat = Math.max(...latitudes);

      const response = await PolygonsAPI.fetchPolygonsByLatLong({
        max_lat,
        max_long,
        min_lat,
        min_long,
      });

      return response.data;
    },

    enabled: Number(zoomQuery) >= ZOOM_THRESHOLD,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  return { data, isLoading, error, refetch, isFetching };
};

export const useGetPolygonsByZone = (
  value: google.maps.LatLngBoundsLiteral,
  cityId: number,
  zoneOverlayName: string,
) => {
  return useQuery(
    ['getPolygonsByZoneType', value, cityId, zoneOverlayName],
    async () => {
      if (!cityId || !zoneOverlayName || !value) {
        return {};
      }

      return fetchPolygonsByZone({
        bounds: value,
        cityId,
        zoneOverlayName,
      });
    },
    { refetchOnWindowFocus: false },
  );
};
